import { ArrowRight, CalendarDays, Newspaper } from "lucide-react";
import { Link } from "wouter";
import { trackRelatedNewsClick } from "@/lib/analytics";
import { ResponsiveImage } from "@/components/ResponsiveImage";

import SchemaScript from "@/components/SchemaScript";
interface RelatedNewsPost {
  slug: string;
  title: string;
  excerpt: string;
  heroImage: string;
  heroAlt?: string;
  date: string;
  tags: string[];
}

interface RelatedNewsProps {
  posts: RelatedNewsPost[];
  currentSlug: string;
}

function formatDate(date: string) {
  return new Date(date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * A deliberately separate, compact module for a curated breaking-news trail.
 * It complements the sitewide Related Articles grid without repeating its links.
 */
export default function RelatedNews({ posts, currentSlug }: RelatedNewsProps) {
  if (!posts.length) return null;

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Related News",
    itemListElement: posts.map((post, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: `https://integrated.social/blog/${post.slug}/`,
      name: post.title,
    })),
  };

  return (
    <section
      aria-labelledby="related-news-heading"
      className="mt-10 rounded-2xl border border-sky-500/20 bg-gradient-to-br from-slate-950 via-slate-900 to-sky-950/40 p-6 sm:p-8"
    >
      <SchemaScript schema={JSON.stringify(itemListSchema)} />
      <div className="flex flex-col gap-3 border-b border-white/10 pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-sky-300">
            <Newspaper className="h-4 w-4" aria-hidden="true" />
            Related News
          </p>
          <h2 id="related-news-heading" className="mt-2 font-serif text-2xl text-white sm:text-3xl">
            Continue the AI safety and governance briefing
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/60">
            Three source-qualified news analyses on agent controls, trusted capability, and governed connected apps.
          </p>
        </div>
        <Link
          href="/blog/topics/ai-news-trends/"
          className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-sky-200 transition-colors hover:text-white"
        >
          All AI news
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-3">
        {posts.map((post, index) => (
          <Link key={post.slug} href={`/blog/${post.slug}/`}>
            <article
              onClick={() => trackRelatedNewsClick({
                from_slug: currentSlug,
                to_slug: post.slug,
                position: index + 1,
              })}
              aria-label={`Read related news: ${post.title}`}
              className="group flex h-full flex-col overflow-hidden rounded-xl border border-white/10 bg-white/[0.04] transition duration-200 hover:-translate-y-0.5 hover:border-sky-300/50 hover:bg-white/[0.08]"
            >
              <div className="aspect-[16/9] overflow-hidden bg-slate-800">
                <ResponsiveImage
                  src={post.heroImage}
                  alt={post.heroAlt || post.title}
                  width={640}
                  height={360}
                  sizes="(min-width: 768px) 30vw, calc(100vw - 48px)"
                  loading="lazy"
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col p-5">
                <div className="flex items-center gap-2 text-xs font-medium text-sky-200/80">
                  <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                  {formatDate(post.date)}
                </div>
                <h3 className="mt-3 font-serif text-lg leading-snug text-white transition-colors group-hover:text-sky-200">
                  {post.title}
                </h3>
                <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-white/60">
                  {post.excerpt}
                </p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-sky-200">
                  Read the analysis
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
                </span>
              </div>
            </article>
          </Link>
        ))}
      </div>
    </section>
  );
}
